// src/ConsumerApp/Screens/Home/components/FeaturedCategoriesSection.js

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowRight from '../../../../assets/icons/ArrowRight.svg';
import ProductCard from '../../ProductsList/components/ProductCard';
import ProductOptionsModal from '../../ProductDetails/components/ProductOptionsModal';

// Shared API Hooks
import {
  useGetBucketSummary,
  useGetProductVariants,
} from '../../../api/products/product.hooks';
import {
  useGetSavedList,
  useAddItemToSavedList,
  useRemoveItemFromSavedList,
} from '../../../api/savedList/savedList.hooks';
import { useGetDefaultDeliveryAddress } from '../../../api/projects/project.hooks';
import { guestBucketManager } from '../../../utils/guestBucketManager';

export default function FeaturedCategoriesSection({ categorySections = [] }) {
  const navigate = useNavigate();

  // 1. Reactive Customer Session
  const [customer, setCustomer] = useState(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const syncAuth = () => {
      try {
        const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
        setCustomer(stored ? JSON.parse(stored) : null);
      } catch {
        setCustomer(null);
      }
    };

    window.addEventListener('auth-state-change', syncAuth);
    window.addEventListener('storage', syncAuth);

    return () => {
      window.removeEventListener('auth-state-change', syncAuth);
      window.removeEventListener('storage', syncAuth);
    };
  }, []);

  const customerId = customer?._id || customer?.id;

  // 2. Guest Bucket State Listener
  const [guestBucketMap, setGuestBucketMap] = useState(() =>
    guestBucketManager.getQuantityMap()
  );

  useEffect(() => {
    const handleGuestChange = () => {
      setGuestBucketMap(guestBucketManager.getQuantityMap());
    };

    window.addEventListener('guest-bucket-change', handleGuestChange);
    return () => window.removeEventListener('guest-bucket-change', handleGuestChange);
  }, []);

  // 3. Options Sheet State
  const [isOptionsSheetVisible, setIsOptionsSheetVisible] = useState(false);
  const [selectedProductWithOptions, setSelectedProductWithOptions] = useState([]);
  const [isOptionsAvailableForBuyNow, setIsOptionsAvailableForBuyNow] = useState(true);

  // 4. API Queries & Mutations
  const { data: rawAddress } = useGetDefaultDeliveryAddress(customerId);
  const { data: bucketSummary } = useGetBucketSummary(customerId);
  const { data: savedList = [] } = useGetSavedList(customerId);

  const addItemToSavedMutation = useAddItemToSavedList();
  const removeItemFromSavedMutation = useRemoveItemFromSavedList();
  const { mutateAsync: fetchProductVariants } = useGetProductVariants();

  const hubId =
    rawAddress?.data?.address?.hub_id ||
    rawAddress?.data?.hub_id ||
    rawAddress?.hub_id ||
    customer?.hubId ||
    customer?.hub_id;

  const savedItemsSet = useMemo(() => {
    const list = Array.isArray(savedList) ? savedList : savedList?.items || [];
    return new Set(
      list.map((item) => String(item.product_id || item.item_id || item._id))
    );
  }, [savedList]);

  const bucketItemsMap = useMemo(() => {
    const map = new Map();
    const buyNowItems = bucketSummary?.buy_now_bucket?.items || [];
    buyNowItems.forEach((bItem) => {
      if (bItem?.item_id) {
        map.set(String(bItem.item_id), {
          quantity: bItem.quantity,
          bucketId:
            bucketSummary?.buy_now_bucket?.bucket_id ||
            bucketSummary?.buy_now_bucket?._id,
        });
      }
    });
    return map;
  }, [bucketSummary]);

  const getProductQuantity = (pid) => {
    if (customerId) {
      return bucketItemsMap.get(pid)?.quantity;
    }
    return guestBucketMap.get(pid);
  };

  const handleToggleSave = async (item, shouldSave) => {
    if (!customerId) {
      navigate('/consumer');
      return false;
    }
    const pid = String(item.product_id || item._id || item.id);
    try {
      if (shouldSave) {
        await addItemToSavedMutation.mutateAsync({
          customer_id: customerId,
          product_id: pid,
        });
      } else {
        await removeItemFromSavedMutation.mutateAsync({
          customer_id: customerId,
          product_id: pid,
        });
      }
      return true;
    } catch (err) {
      console.error('Failed to toggle saved item:', err);
      return false;
    }
  };

  const handleOpenOptions = async (productItem, isBuyNowAvailable) => {
    const pid = String(productItem.product_id || productItem._id);
    try {
      const purchase_mode = isBuyNowAvailable ? 'Buy Now' : 'Quote';
      const variantsResponse = await fetchProductVariants({
        product_id: pid,
        purchase_mode,
      });
      const list = Array.isArray(variantsResponse)
        ? variantsResponse
        : variantsResponse?.product_variants || [];

      setSelectedProductWithOptions(list);
      setIsOptionsAvailableForBuyNow(isBuyNowAvailable);
      setIsOptionsSheetVisible(true);
    } catch (error) {
      console.error('Error fetching product options:', error);
    }
  };

  if (!categorySections || categorySections.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, width: '100%' }}>
      <style>{`
        /* Self-contained, guaranteed right-aligned header row */
        .bb-cat-section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          margin-bottom: 12px;
          box-sizing: border-box;
        }

        .bb-cat-title-cluster {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          min-width: 0;
          flex: 1;
        }

        .bb-cat-heading {
          font-size: clamp(15px, 2vw, 18px);
          font-weight: 800;
          color: #2B2A29;
          margin: 0;
          letter-spacing: -0.2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .bb-cat-see-all-btn {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          cursor: pointer;
          flex-shrink: 0;
          margin-left: auto; /* Guarantees hard right positioning */
          padding: 4px 0 4px 12px;
          user-select: none;
        }

        .bb-cat-see-all-text {
          font-size: 13px;
          font-weight: 800;
          color: #E9001E;
          letter-spacing: 0.1px;
        }

        .featured-products-scroll {
          display: flex;
          overflow-x: auto;
          overflow-y: visible;
          gap: 16px;
          padding: 6px 2px 18px 2px;
          scroll-snap-type: x mandatory;
          scrollbar-width: none;
          -webkit-overflow-scrolling: touch;
          width: 100%;
          box-sizing: border-box;
        }

        .featured-products-scroll::-webkit-scrollbar {
          display: none;
        }

        .featured-card-wrapper {
          width: 176px;
          min-width: 176px;
          flex-shrink: 0;
          scroll-snap-align: start;
          box-sizing: border-box;
        }

        @media (max-width: 768px) {
          .featured-card-wrapper {
            width: 160px;
            min-width: 160px;
          }
        }

        @media (max-width: 480px) {
          .featured-card-wrapper {
            width: 146px;
            min-width: 146px;
          }
          .featured-products-scroll {
            gap: 12px;
          }
        }
      `}</style>

      {categorySections.map((section) => {
        const {
          product_category_id,
          product_category_name,
          products = [],
        } = section;

        if (!products || products.length === 0) return null;

        const handleNavigateToCategory = () => {
          navigate('/consumer/productslist', {
            state: {
              categoryId: product_category_id,
              categoryName: product_category_name,
            },
          });
        };

        return (
          <section key={product_category_id} style={{ width: '100%' }}>
            {/* Header: Left Category Name + Right "See All" */}
            <div className="bb-cat-section-header">
              <div
                className="bb-cat-title-cluster"
                onClick={handleNavigateToCategory}
              >
                <h3 className="bb-cat-heading">{product_category_name}</h3>
              </div>

              <div
                className="bb-cat-see-all-btn"
                onClick={handleNavigateToCategory}
                role="button"
                tabIndex={0}
              >
                <span className="bb-cat-see-all-text">See All</span>
                <img src={ArrowRight} alt="arrow" style={{ width: 12, height: 12 }} />
              </div>
            </div>

            {/* Horizontal Product Rail */}
            <div className="featured-products-scroll">
              {products.map((prod) => {
                const pid = String(prod.product_id || prod._id);
                const quantity = getProductQuantity(pid);
                const bucketEntry = customerId ? bucketItemsMap.get(pid) : null;

                return (
                  <div key={pid} className="featured-card-wrapper">
                    <ProductCard
                      item={prod}
                      customerId={customerId}
                      hubId={hubId}
                      savedBucketQuantity={quantity}
                      bucketId={bucketEntry?.bucketId}
                      isSavedItem={savedItemsSet.has(pid)}
                      onToggleSave={handleToggleSave}
                      onOptionsClick={handleOpenOptions}
                      onItemClick={() =>
                        navigate('/consumer/productdetails', {
                          state: { productId: pid },
                        })
                      }
                    />
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}

      <ProductOptionsModal
        visible={isOptionsSheetVisible}
        onClose={() => setIsOptionsSheetVisible(false)}
        cardTitle={isOptionsAvailableForBuyNow ? 'Add To Buy' : 'Add To Quote'}
        productData={selectedProductWithOptions}
        bucketItemsMap={bucketItemsMap}
        customerId={customerId}
        hubId={hubId}
        purchaseType={isOptionsAvailableForBuyNow ? 'BUY_NOW' : 'RFQ'}
      />
    </div>
  );
}